import { useEffect, useState } from "react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import CustomizeModal from "@/components/CustomizeModal";
import { T } from "@/context/TextEditContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * FeaturedCarousel — a horizontal auto-advancing image slider shown on the
 * HomePage between the status banner and the menu. Reads
 * `settings.featured_items` (an array of {image_url, caption?}). Renders
 * nothing at all when the array is empty so the section is invisible
 * until the owner adds pictures via Admin → Site → Featured Items.
 */
export default function FeaturedCarousel() {
  const { settings } = useSiteSettings();
  const items = Array.isArray(settings?.featured_items)
    ? settings.featured_items.filter((it) => it && it.image_url)
    : [];
  const [idx, setIdx] = useState(0);
  const [menuMap, setMenuMap] = useState({}); // id -> menu item
  const [customizeItem, setCustomizeItem] = useState(null);
  const cart = useCart();

  // Pull the menu lazily so we can resolve `item_id` → menu item when the
  // customer taps a slide. Fetch only if at least one slide has an item_id.
  useEffect(() => {
    if (!items.some((it) => it.item_id)) return;
    let cancelled = false;
    api.get("/menu")
      .then(({ data }) => {
        if (cancelled) return;
        const m = {};
        for (const it of data) m[it.id] = it;
        setMenuMap(m);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [items]);

  const handleSlideClick = (slide) => {
    if (!slide.item_id) return;
    const menuItem = menuMap[slide.item_id];
    if (!menuItem) return;
    // Sold out or unavailable — no-op (no visual indication because most
    // owners won't feature sold-out items on purpose)
    if (menuItem.sold_out || menuItem.hidden) return;
    // Customizable items open the modal; simple items add directly.
    if (menuItem.options && menuItem.options.length > 0) {
      setCustomizeItem(menuItem);
    } else {
      cart.add(menuItem, {}, 1);
    }
  };

  // Auto-advance every 5 s. Pauses if there's only one slide (nothing to
  // advance to) or if the tab is hidden (saves battery on the truck iPad).
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => {
      if (document.hidden) return;
      setIdx((i) => (i + 1) % items.length);
    }, 5000);
    return () => clearInterval(t);
  }, [items.length]);

  // Clamp the index if items shrink (owner deleted a slide).
  useEffect(() => {
    if (idx >= items.length && items.length > 0) setIdx(0);
  }, [items.length, idx]);

  if (items.length === 0) return null;

  const go = (n) => setIdx((n + items.length) % items.length);
  const current = items[idx];

  return (
    <section
      data-testid="featured-carousel"
      className="mx-4 mt-3 rounded-2xl overflow-hidden relative bg-stone-900 shadow-lg"
      style={{ aspectRatio: "16 / 9" }}
    >
      {/* Slide track — translateX by (100% of parent width) per index.
          Inner flex container is `absolute inset-0` so its width equals the
          parent's width; each slide is `w-full h-full` = one parent-width.
          translateX(-idx * 100%) then advances exactly one slide at a time. */}
      <div
        className="absolute inset-0 flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {items.map((it, i) => {
          const isClickable = !!(it.item_id && menuMap[it.item_id]);
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleSlideClick(it)}
              disabled={!isClickable}
              data-testid={`featured-slide-${i}`}
              className={`relative w-full h-full flex-shrink-0 text-left ${
                isClickable ? "cursor-pointer group" : "cursor-default"
              }`}
              aria-label={it.caption ? it.caption : `Featured item ${i + 1}`}
            >
              <img
                src={it.image_url}
                alt={it.caption || `Featured item ${i + 1}`}
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
              {it.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4">
                  <div className="font-hand text-brand-yellow text-2xl sm:text-3xl leading-tight">
                    {it.caption}
                  </div>
                  {isClickable && (
                    <div className="mt-2 inline-block bg-brand-red text-white px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-black group-hover:bg-red-800 transition-colors">
                      Add to cart →
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {items.length > 1 && (
        <>
          <button
            onClick={() => go(idx - 1)}
            data-testid="featured-prev"
            aria-label="Previous"
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 backdrop-blur-sm"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => go(idx + 1)}
            data-testid="featured-next"
            aria-label="Next"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 backdrop-blur-sm"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`featured-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "bg-brand-yellow w-6" : "bg-white/50 w-1.5"
                }`}
              />
            ))}
          </div>
        </>
      )}

      {customizeItem && (
        <CustomizeModal
          item={customizeItem}
          onClose={() => setCustomizeItem(null)}
        />
      )}
    </section>
  );
}
